import React, { useState } from "react";
import { Link } from "react-router-dom";
import { doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import "../Styles/ContractTips.css";

const SITE = window.location.origin;

/* Stop the parent <Link> from navigating when clicking buttons inside */
const stop = e => { e.preventDefault(); e.stopPropagation(); };

/* ── LIKE BUTTON ── */
const LikeBtn = ({ slug, initialCount }) => {
  const key = `pp_liked_tip_${slug}`;
  const [liked, setLiked]   = useState(() => !!localStorage.getItem(key));
  const [count, setCount]   = useState(initialCount || 0);
  const [bounce, setBounce] = useState(false);

  const handleLike = e => {
    stop(e);
    if (liked) return;
    setLiked(true);
    setCount(c => c + 1);
    setBounce(true);
    setTimeout(() => setBounce(false), 600);
    localStorage.setItem(key, "1");
    updateDoc(doc(db, "contract_tips", slug), { likes: increment(1) }).catch(() => {});
  };

  return (
    <button
      className={`tip-card-like${liked ? " liked" : ""}${bounce ? " bounce" : ""}`}
      onClick={handleLike}
      title={liked ? "You liked this!" : "Like this tip"}
      aria-label={liked ? "Liked" : "Like"}
    >
      <span>{liked ? "♥" : "♡"}</span>
      {count > 0 && <span className="tip-card-like-count">{count}</span>}
    </button>
  );
};

/* ── SHARE BUTTON ── */
const ShareBtn = ({ slug }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = e => {
    stop(e);
    const url = `${SITE}/contract-tips/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <button
      className={`tip-card-share${copied ? " copied" : ""}`}
      onClick={handleShare}
      title={copied ? "Link copied!" : "Copy link"}
      aria-label="Share"
    >
      {copied ? "✓" : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
      )}
    </button>
  );
};

/* ── CARD ── */
const ContractTipCard = ({ tip, featured = false }) => {
  if (!tip) return null;

  const slug  = tip.slug || tip.id;
  const thumb = tip.thumbnail || tip.coverImage || "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80";
  const mins  = tip.content
    ? Math.max(1, Math.ceil(tip.content.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length / 200))
    : null;

  if (featured) {
    return (
      <Link to={`/contract-tips/${slug}`} className="tip-card-link tip-featured-link">
        <article className="tip-card tip-card--featured">
          <div
            className="tip-featured-img"
            style={{ backgroundImage: `url(${thumb})` }}
            aria-hidden="true"
          >
            <div className="tip-featured-overlay" />
            <span className="tip-category">{tip.category}</span>
            <span className="featured-badge">✦ Featured</span>
          </div>

          <div className="tip-featured-body">
            {tip.subtitle && <p className="tip-subtitle">{tip.subtitle}</p>}
            <h2 className="tip-title">{tip.title}</h2>
            <p className="tip-summary">{tip.summary}</p>

            {tip.tags && tip.tags.length > 0 && (
              <div className="tip-tags">
                {tip.tags.slice(0, 4).map((tag, i) => (
                  <span key={i} className="tip-tag">#{tag}</span>
                ))}
              </div>
            )}

            {tip.author && (
              <div className="tip-meta">
                <img
                  src={tip.author.avatar}
                  alt={tip.author.name}
                  className="tip-author-avatar"
                  onError={e => { e.target.style.display = "none"; }}
                />
                <div className="tip-author-info">
                  <span className="tip-author-name">{tip.author.name}</span>
                  <span className="tip-author-role">{tip.author.role}</span>
                </div>
              </div>
            )}

            <div className="tip-card-footer">
              <span className="tip-cta">Read tip <span aria-hidden="true">→</span></span>
              <div className="tip-card-actions">
                {mins && <span className="tip-card-readtime">⏱ {mins} min</span>}
                {tip.views > 0 && <span className="tip-card-views">👁 {tip.views.toLocaleString()}</span>}
                <LikeBtn slug={slug} initialCount={tip.likes || 0} />
                <ShareBtn slug={slug} />
              </div>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/contract-tips/${slug}`} className="tip-card-link">
      <article className="tip-card">
        <div
          className="tip-thumbnail"
          style={{ backgroundImage: `url(${thumb})` }}
          aria-hidden="true"
        >
          <div className="tip-overlay">
            <span className="tip-category">{tip.category}</span>
          </div>
        </div>

        <div className="tip-content">
          {tip.subtitle && <p className="tip-subtitle">{tip.subtitle}</p>}
          <h3 className="tip-title">{tip.title}</h3>
          <p className="tip-summary">{tip.summary}</p>

          {tip.author && (
            <div className="tip-meta">
              <img
                src={tip.author.avatar}
                alt={tip.author.name}
                className="tip-author-avatar"
                onError={e => { e.target.style.display = "none"; }}
              />
              <div className="tip-author-info">
                <span className="tip-author-name">{tip.author.name}</span>
                <span className="tip-author-role">{tip.author.role}</span>
              </div>
            </div>
          )}

          {tip.tags && tip.tags.length > 0 && (
            <div className="tip-tags">
              {tip.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="tip-tag">#{tag}</span>
              ))}
            </div>
          )}

          <div className="tip-card-footer">
            <span className="tip-cta-small">Read tip →</span>
            <div className="tip-card-actions">
              {mins && <span className="tip-card-readtime">⏱ {mins} min</span>}
              {tip.views > 0 && <span className="tip-card-views">👁 {tip.views.toLocaleString()}</span>}
              <LikeBtn slug={slug} initialCount={tip.likes || 0} />
              <ShareBtn slug={slug} />
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default ContractTipCard;
